<template>
  <div style="width: 100%; padding: 10px">
      <h1>{{ $t("fitness.header") }} - {{ $t("fitness.stats") }}</h1>
    <h3 style="margin: 10px">{{ $t("fitness.all_time") }}</h3>

    <!-- All time results -->
    <v-layout
      wrap
      row
      style="margin-left: 0; margin-right: 0; margin-top: 20px"
    >
      <info-tile :name="$t('fitness.all_time_steps')" :data="all_time_steps" icon="shoe-prints"></info-tile>
      <info-tile :name="$t('fitness.all_time_average_steps')" :data="all_time_average_steps" icon="shoe-prints"></info-tile>
      <info-tile :name="$t('fitness.all_time_floors')" :data="all_time_floors" icon="level-up"></info-tile>
      <info-tile :name="$t('fitness.all_time_distance')" :data="all_time_distance" icon="map"></info-tile>
    </v-layout>

    <h3 style="margin: 10px; margin-top: 40px">{{ $t("fitness.history_insights") }}</h3>

    <v-layout row wrap style="margin: 10px; align-items: center;">
      <v-flex xs12 sm4 md4 style="padding: 5px;">
        <v-select
          v-model="selectedRange"
          :items="rangeOptions"
          :label="$t('fitness.time_range')"
          outlined
          dense
          hide-details
          @change="onRangeChange"
        ></v-select>
      </v-flex>
      <v-flex xs12 sm4 md4 style="padding: 5px;">
        <v-select
          v-model="selectedGrouping"
          :items="groupingOptions"
          :label="$t('fitness.group_by')"
          outlined
          dense
          hide-details
          @change="updateChart"
        ></v-select>
      </v-flex>
      <v-flex xs12 sm4 md4 style="padding: 5px;">
        <v-select
          v-model="selectedMetric"
          :items="metricOptions"
          :label="$t('fitness.metric')"
          outlined
          dense
          hide-details
          @change="updateChart"
        ></v-select>
      </v-flex>
    </v-layout>

    <v-layout row wrap style="margin: 5px; margin-top: 20px;">
        <v-flex xs12 sm12>
            <div v-if="loadingHistory" style="text-align: center; margin-top: 40px;">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="chartOptions.xaxis.categories.length > 0">
                <apexchart :type="chartType" height="350" :options="chartOptions" :series="series"></apexchart>
            </div>
            <div v-else style="text-align: center; margin-top: 20px;">
                <p>{{ $t("fitness.no_data_here") }}</p>
            </div>
        </v-flex>
    </v-layout>

    <div v-if="insights.length > 0">
        <h3 style="margin: 10px; margin-top: 40px">{{ $t("fitness.dynamic_insights") }}</h3>
        <v-layout row wrap style="margin: 5px;">
            <v-flex 
              v-for="(insight, index) in insights" 
              :key="index" 
              xs12 
              :sm12="index < 3" 
              :sm6="index >= 3" 
              :md4="index < 3" 
              :md3="index >= 3" 
              style="padding: 5px;">
                <v-card 
                  outlined 
                  height="100%" 
                  :style="index < 3 ? 'background-color: #FFB024; border-color: #FFB024;' : ''"
                >
                    <v-card-title :style="index < 3 ? 'font-size: 20px; font-weight: bold; word-break: break-word; color: #1a1a1a;' : 'font-size: 16px; word-break: break-word;'">
                        <i :class="'fad fa-' + insight.icon" :style="index < 3 ? 'margin-right: 10px; color: #1a1a1a;' : 'margin-right: 10px; color: #FFB024;'"></i> 
                        {{ insight.title }}
                    </v-card-title>
                    <v-card-text :style="index < 3 ? 'font-size: 16px; color: #1a1a1a;' : ''" v-html="insight.text"></v-card-text>
                </v-card>
            </v-flex>
        </v-layout>
    </div>

  </div>
</template>

<script>
import InfoTile from '../components/fitness/InfoTile.vue';

import { storage } from '../schmucklicloud';
var numeral = require("numeral");

export default {
  components: { InfoTile },
  data() {
    return {
      all_time_steps: 0,
      all_time_average_steps: 0,
      all_time_distance: 0,
      all_time_floors: 0,

      loadingHistory: false,
      fitness_history: [],
      
      insights: [],

      selectedRange: 'all',
      selectedGrouping: 'year',
      selectedMetric: 'steps',

      series: [{
        name: 'Steps',
        data: []
      }],
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
              show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            endingShape: 'rounded'
          },
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          title: {
            text: 'Steps'
          }
        },
        fill: {
          opacity: 1,
          colors: ['#FFB024']
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return numeral(val).format("0,000").replace(/,/g, "'") + " steps"
            }
          }
        }
      }
    };
  },
  computed: {
      chartType() {
          let lineMetrics = ['sleep', 'weight', 'body_fat', 'muscle_mass', 'bone_mass', 'hydration', 'spo2', 'body_temperature'];
          return lineMetrics.includes(this.selectedMetric) ? 'line' : 'bar';
      },
      rangeOptions() {
          return [
              { text: this.$t('fitness.last_30_days'), value: '30' },
              { text: this.$t('fitness.last_3_months'), value: '90' },
              { text: this.$t('fitness.last_6_months'), value: '180' },
              { text: this.$t('fitness.last_year'), value: '365' },
              { text: this.$t('fitness.all_time'), value: 'all' }
          ];
      },
      groupingOptions() {
          return [
              { text: this.$t('fitness.by_day'), value: 'day' },
              { text: this.$t('fitness.by_month'), value: 'month' },
              { text: this.$t('fitness.by_year'), value: 'year' }
          ];
      },
      metricOptions() {
          return [
              { text: this.$t('fitness.steps'), value: 'steps' },
              { text: this.$t('fitness.average_sleep'), value: 'sleep' },
              { text: this.$t('fitness.floors'), value: 'floors' },
              { text: this.$t('fitness.distance') + ' (km)', value: 'distance' },
              { text: this.$t('fitness.weight'), value: 'weight' },
              { text: this.$t('fitness.body_fat'), value: 'body_fat' },
              { text: this.$t('fitness.muscle_mass'), value: 'muscle_mass' },
              { text: this.$t('fitness.bone_mass'), value: 'bone_mass' },
              { text: this.$t('fitness.hydration'), value: 'hydration' },
              { text: this.$t('fitness.spo2'), value: 'spo2' },
              { text: this.$t('fitness.body_temperature'), value: 'body_temperature' }
          ];
      }
  },
  mounted() {
    this.loadTotalSteps();
    this.loadTotalAverageSteps();
    this.loadTotalDistance();
    this.loadTotalFloors();
    
    this.loadHistoryData();
  },
  methods: {
    async loadTotalSteps() {
        var response_all_time_steps = await storage.sum(
        "fitness_daily",
        [
          {
            column: "date",
            operator: "contains",
            value: "-",
          },
        ],
        "steps"
      );

      if (response_all_time_steps.isOK) {
        this.all_time_steps = numeral(response_all_time_steps.data.sum)
          .format("0,000")
          .replace(/,/g, "'");
      }
    },
    async loadTotalAverageSteps() {
        var response_all_time_avg_steps = await storage.avg(
        "fitness_daily",
        [
          {
            column: "date",
            operator: "contains",
            value: "-",
          },
        ],
        "steps"
      );
      if (response_all_time_avg_steps.isOK) {
        this.all_time_average_steps = numeral(
          response_all_time_avg_steps.data.avg
        )
          .format("0,000")
          .replace(/,/g, "'");
      }
    },
    async loadTotalDistance() {
      var response_all_time_distance = await storage.sum(
        "fitness_daily",
        [
          {
            column: "date",
            operator: "contains",
            value: "-",
          },
        ],
        "distance"
      );
      if (response_all_time_distance.isOK) {
        this.all_time_distance = numeral(response_all_time_distance.data.sum)
          .format("0,000.00")
          .replace(/,/g, "'");
      }
    },
    async loadTotalFloors() {
      var response_all_time_distance = await storage.sum(
        "fitness_daily",
        [
          {
            column: "date",
            operator: "contains",
            value: "-",
          },
        ],
        "floors"
      );
      if (response_all_time_distance.isOK) {
        this.all_time_floors = numeral(Math.floor(response_all_time_distance.data.sum))
          .format("0,000")
          .replace(/,/g, "'");
      }
    },

    getHoursAndMinutesFromMinutes(input_minutes) {
      var minutes = parseInt(input_minutes);
      var hours = Math.floor(minutes / 60);
      minutes = minutes % 60;
      return hours + "h " + minutes + "m";
    },

    async loadHistoryData() {
        this.loadingHistory = true;
        var response = await storage.get("fitness_daily", [
            {
                column: "date",
                operator: "contains",
                value: "-",
            },
        ]);
        
        if (response.isOK && response.data) {
            this.fitness_history = response.data;
            this.updateChart();
        }
        this.loadingHistory = false;
    },
    
    onRangeChange() {
        if (this.selectedRange === '30') {
            this.selectedGrouping = 'day';
        } else if (this.selectedRange === '90' || this.selectedRange === '180' || this.selectedRange === '365') {
            this.selectedGrouping = 'month';
        } else if (this.selectedRange === 'all') {
            this.selectedGrouping = 'year';
        }
        this.updateChart();
    },

    formatDate(dateStr) {
        if (!dateStr) return '';
        var months = [
            this.$t("general.month.january"),
            this.$t("general.month.february"),
            this.$t("general.month.march"),
            this.$t("general.month.april"),
            this.$t("general.month.may"),
            this.$t("general.month.june"),
            this.$t("general.month.july"),
            this.$t("general.month.august"),
            this.$t("general.month.september"),
            this.$t("general.month.october"),
            this.$t("general.month.november"),
            this.$t("general.month.december"),
        ];

        let parts = dateStr.split('-');
        if (parts.length === 3) {
            let monthName = months[parseInt(parts[1]) - 1];
            return `${parseInt(parts[2])}. ${monthName} ${parts[0]}`;
        } else if (parts.length === 2) {
            let monthName = months[parseInt(parts[1]) - 1];
            return `${monthName} ${parts[0]}`;
        }
        return dateStr;
    },
    
    formatDateLink(dateStr) {
        if (!dateStr) return '';
        let formatted = this.formatDate(dateStr);
        return `<a href="#/dashboard/${dateStr}" style="text-decoration: underline; color: inherit; font-weight: bold;">${formatted}</a>`;
    },
    
    generateInsights(data) {
        this.insights = [];
        if (!data || data.length === 0) return;

        let maxStepsDay = null;
        let maxSteps = 0;
        
        let totalDistance = 0;

        let maxSleepDay = null;
        let maxSleep = 0;

        let maxFloorsDay = null;
        let maxFloors = 0;

        let currentStreak = 0;
        let maxStreak = 0;
        
        let maxCaloriesDay = null;
        let maxCalories = 0;

        let totalSteps = 0;
        let minStepsDay = null;
        let minSteps = Infinity;
        let daysOver7HoursSleep = 0;
        let daysWithSteps = 0;
        
        let maxWeekendDay = null;
        let maxWeekendSteps = 0;

        let minHrDay = null;
        let minHr = Infinity;
        
        let maxDistanceDay = null;
        let maxDistance = 0;

        let minSleepDay = null;
        let minSleep = Infinity;

        let goalDays = 0;

        let firstWeight = null;
        let lastWeight = null;
        
        let minBodyFatDay = null;
        let minBodyFat = Infinity;

        let maxMuscleDay = null;
        let maxMuscle = 0;

        let sortedData = [...data].sort((a, b) => a.data.date.localeCompare(b.data.date));

        sortedData.forEach(item => {
            let d = item.data;
            let s = parseInt(d.steps) || 0;
            let dist = parseFloat(d.distance) || 0;
            let sleep = parseInt(d.sleep_duration) || 0;
            let floors = parseInt(d.floors) || 0;
            let cals = parseInt(d.calories) || 0;

            totalDistance += dist;
            totalSteps += s;

            if (s > 0) {
                daysWithSteps++;
                if (s < minSteps) {
                    minSteps = s; minStepsDay = d.date;
                }
            }

            if (sleep >= 420) {
                daysOver7HoursSleep++;
            }

            if (s > maxSteps) { maxSteps = s; maxStepsDay = d.date; }
            if (floors > maxFloors) { maxFloors = floors; maxFloorsDay = d.date; }
            if (cals > maxCalories) { maxCalories = cals; maxCaloriesDay = d.date; }
            
            if (sleep > 0) {
                if (sleep > maxSleep) { maxSleep = sleep; maxSleepDay = d.date; }
            }
            
            if (s >= 10000) {
                currentStreak++;
                if (currentStreak > maxStreak) {
                    maxStreak = currentStreak;
                }
            } else {
                currentStreak = 0;
            }
            
            let hr = parseInt(d.heart_rate) || 0;
            if (hr > 0 && hr < minHr) { minHr = hr; minHrDay = d.date; }

            let dateObj = new Date(d.date);
            if (dateObj.getDay() === 0 || dateObj.getDay() === 6) { 
                if (s > maxWeekendSteps) { maxWeekendSteps = s; maxWeekendDay = d.date; }
            }
            
            if (dist > maxDistance) { maxDistance = dist; maxDistanceDay = d.date; }
            if (sleep > 0 && sleep < minSleep) { minSleep = sleep; minSleepDay = d.date; }
            if (s >= 10000) { goalDays++; }
            
            let w = parseFloat(d.weight) || 0;
            let bf = parseFloat(d.body_fat) || 0;
            let mm = parseFloat(d.muscle_mass) || 0;

            if (w > 0) {
                if (firstWeight === null) firstWeight = w;
                lastWeight = w;
            }

            if (bf > 0 && bf < minBodyFat) {
                minBodyFat = bf;
                minBodyFatDay = d.date;
            }

            if (mm > maxMuscle) {
                maxMuscle = mm;
                maxMuscleDay = d.date;
            }
        });

        if (totalSteps > 0) {
            this.insights.push({
                icon: 'walking',
                title: this.$t("fitness.insight_total_steps_title"),
                text: this.$t("fitness.insight_total_steps_text", {
                    steps: numeral(totalSteps).format("0,000").replace(/,/g, "'")
                })
            });

            let avgSteps = Math.round(totalSteps / daysWithSteps);
            this.insights.push({
                icon: 'chart-line',
                title: this.$t("fitness.insight_avg_steps_title"),
                text: this.$t("fitness.insight_avg_steps_text", {
                    steps: numeral(avgSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (maxStepsDay && maxSteps > 0) {
            this.insights.push({
                icon: 'shoe-prints',
                title: this.$t("fitness.insight_most_active_day_title"),
                text: this.$t("fitness.insight_most_active_day_text", {
                    date: this.formatDateLink(maxStepsDay),
                    steps: numeral(maxSteps).format("0,000").replace(/,/g, "'")
                }),
                date: maxStepsDay
            });
        }

        if (totalDistance > 0) {
            let marathons = (totalDistance / 42.195).toFixed(1);
            this.insights.push({
                icon: 'map',
                title: this.$t("fitness.insight_distance_milestone_title"),
                text: this.$t("fitness.insight_distance_milestone_text", {
                    distance: numeral(totalDistance).format("0,000.0").replace(/,/g, "'"),
                    marathons: marathons
                })
            });
        }

        if (maxSleepDay && maxSleep > 0) {
            let sleepText = this.getHoursAndMinutesFromMinutes(maxSleep);
            this.insights.push({
                icon: 'moon',
                title: this.$t("fitness.insight_best_sleep_title"),
                text: this.$t("fitness.insight_best_sleep_text", {
                    date: this.formatDateLink(maxSleepDay),
                    sleep: sleepText
                }),
                date: maxSleepDay
            });
        }

        if (maxFloorsDay && maxFloors > 0) {
            this.insights.push({
                icon: 'level-up',
                title: this.$t("fitness.insight_peak_climbing_title"),
                text: this.$t("fitness.insight_peak_climbing_text", {
                    date: this.formatDateLink(maxFloorsDay),
                    floors: numeral(maxFloors).format("0,000").replace(/,/g, "'")
                }),
                date: maxFloorsDay
            });
        }

        if (maxStreak > 1) {
            this.insights.push({
                icon: 'fire-alt',
                title: this.$t("fitness.insight_active_streak_title"),
                text: this.$t("fitness.insight_active_streak_text", {
                    streak: maxStreak
                })
            });
        }
        
        if (maxCaloriesDay && maxCalories > 0) {
            this.insights.push({
                icon: 'flame',
                title: this.$t("fitness.insight_calorie_burner_title"),
                text: this.$t("fitness.insight_calorie_burner_text", {
                    date: this.formatDateLink(maxCaloriesDay),
                    calories: numeral(maxCalories).format("0,000").replace(/,/g, "'")
                }),
                date: maxCaloriesDay
            });
        }

        if (minStepsDay && minSteps !== Infinity) {
            this.insights.push({
                icon: 'battery-quarter',
                title: this.$t("fitness.insight_rest_day_title"),
                text: this.$t("fitness.insight_rest_day_text", {
                    date: this.formatDateLink(minStepsDay),
                    steps: numeral(minSteps).format("0,000").replace(/,/g, "'")
                }),
                date: minStepsDay
            });
        }

        if (daysOver7HoursSleep > 0) {
            this.insights.push({
                icon: 'bed',
                title: this.$t("fitness.insight_sleep_consistency_title"),
                text: this.$t("fitness.insight_sleep_consistency_text", {
                    days: daysOver7HoursSleep
                })
            });
        }
        
        if (maxWeekendDay && maxWeekendSteps > 0) {
            this.insights.push({
                icon: 'calendar-star',
                title: this.$t("fitness.insight_weekend_warrior_title"),
                text: this.$t("fitness.insight_weekend_warrior_text", {
                    date: this.formatDateLink(maxWeekendDay),
                    steps: numeral(maxWeekendSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (minHrDay && minHr !== Infinity && minHr > 30) {
            this.insights.push({
                icon: 'heartbeat',
                title: this.$t("fitness.insight_lowest_hr_title"),
                text: this.$t("fitness.insight_lowest_hr_text", {
                    date: this.formatDateLink(minHrDay),
                    hr: minHr
                })
            });
        }
        
        let bounceBackSteps = 0;
        let bounceBackDate = null;
        let restDate = null;
        if (minStepsDay && minSteps !== Infinity) {
            let minDateObj = new Date(minStepsDay);
            minDateObj.setDate(minDateObj.getDate() + 1);
            let m = (minDateObj.getMonth() + 1).toString().padStart(2, '0');
            let day = minDateObj.getDate().toString().padStart(2, '0');
            let nextDayStr = `${minDateObj.getFullYear()}-${m}-${day}`;
            let nextDayItem = sortedData.find(item => item.data.date === nextDayStr);
            if (nextDayItem) {
                bounceBackSteps = parseInt(nextDayItem.data.steps) || 0;
                if (bounceBackSteps > minSteps * 2 && bounceBackSteps > 5000) {
                    bounceBackDate = nextDayStr;
                    restDate = minStepsDay;
                }
            }
        }

        if (bounceBackDate && bounceBackSteps > 0) {
            this.insights.push({
                icon: 'history',
                title: this.$t("fitness.insight_bounce_back_title"),
                text: this.$t("fitness.insight_bounce_back_text", {
                    restDate: this.formatDateLink(restDate),
                    date: this.formatDateLink(bounceBackDate),
                    steps: numeral(bounceBackSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }
        
        if (maxDistanceDay && maxDistance > 0) {
            this.insights.push({
                icon: 'route',
                title: this.$t("fitness.insight_max_distance_title"),
                text: this.$t("fitness.insight_max_distance_text", {
                    date: this.formatDateLink(maxDistanceDay),
                    distance: numeral(maxDistance).format("0,000.0").replace(/,/g, "'")
                })
            });
        }

        if (minSleepDay && minSleep !== Infinity) {
            let sleepText = this.getHoursAndMinutesFromMinutes(minSleep);
            this.insights.push({
                icon: 'coffee',
                title: this.$t("fitness.insight_min_sleep_title"),
                text: this.$t("fitness.insight_min_sleep_text", {
                    date: this.formatDateLink(minSleepDay),
                    sleep: sleepText
                })
            });
        }

        if (goalDays > 0) {
            this.insights.push({
                icon: 'bullseye-arrow',
                title: this.$t("fitness.insight_goal_crusher_title"),
                text: this.$t("fitness.insight_goal_crusher_text", {
                    days: goalDays
                })
            });
        }
        
        if (firstWeight !== null && lastWeight !== null && firstWeight !== lastWeight) {
            let diff = parseFloat(Math.abs(firstWeight - lastWeight).toFixed(2));
            if (diff > 0) {
                if (firstWeight > lastWeight) {
                    this.insights.push({
                        icon: 'weight',
                        title: this.$t("fitness.insight_weight_journey_title"),
                        text: this.$t("fitness.insight_weight_journey_lost_text", {
                            diff: numeral(diff).format("0.00").replace(/,/g, "'")
                        })
                    });
                } else {
                    this.insights.push({
                        icon: 'weight',
                        title: this.$t("fitness.insight_weight_journey_title"),
                        text: this.$t("fitness.insight_weight_journey_gained_text", {
                            diff: numeral(diff).format("0.00").replace(/,/g, "'")
                        })
                    });
                }
            }
        }

        if (minBodyFatDay && minBodyFat !== Infinity) {
            this.insights.push({
                icon: 'percentage',
                title: this.$t("fitness.insight_lowest_body_fat_title"),
                text: this.$t("fitness.insight_lowest_body_fat_text", {
                    date: this.formatDateLink(minBodyFatDay),
                    body_fat: numeral(minBodyFat).format("0.00").replace(/,/g, "'")
                })
            });
        }

        if (maxMuscleDay && maxMuscle > 0) {
            this.insights.push({
                icon: 'dumbbell',
                title: this.$t("fitness.insight_peak_muscle_mass_title"),
                text: this.$t("fitness.insight_peak_muscle_mass_text", {
                    date: this.formatDateLink(maxMuscleDay),
                    muscle: numeral(maxMuscle).format("0.00").replace(/,/g, "'")
                })
            });
        }

        // Shuffle insights array randomly
        for (let i = this.insights.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.insights[i], this.insights[j]] = [this.insights[j], this.insights[i]];
        }
    },

    updateChart() {
        if (!this.fitness_history || this.fitness_history.length === 0) return;

        let filteredHistory = this.fitness_history;
        if (this.selectedRange !== 'all') {
            let daysToSubtract = parseInt(this.selectedRange);
            let cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - daysToSubtract);
            
            filteredHistory = this.fitness_history.filter(item => {
                if (!item.data || !item.data.date) return false;
                return new Date(item.data.date) >= cutoffDate;
            });
        }

        this.generateInsights(filteredHistory);

        let grouped = {};

        let startDate;
        let endDate = new Date();
        endDate.setHours(0, 0, 0, 0);
        
        if (this.selectedRange !== 'all') {
            let daysToSubtract = parseInt(this.selectedRange);
            startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - daysToSubtract);
        } else {
            if (filteredHistory.length > 0) {
                let sortedDates = filteredHistory.map(item => item.data.date).sort();
                startDate = new Date(sortedDates[0]);
                startDate.setHours(0, 0, 0, 0);
            } else {
                startDate = new Date(endDate);
            }
        }

        if (startDate && endDate) {
            let current = new Date(startDate);
            if (this.selectedGrouping === 'month') {
                current.setDate(1);
            } else if (this.selectedGrouping === 'year') {
                current.setDate(1);
                current.setMonth(0);
            }

            while (current <= endDate) {
                let y = current.getFullYear();
                let m = (current.getMonth() + 1).toString().padStart(2, '0');
                let d = current.getDate().toString().padStart(2, '0');
                
                let groupKey;
                if (this.selectedGrouping === 'year') {
                    groupKey = y.toString();
                } else if (this.selectedGrouping === 'month') {
                    groupKey = `${y}-${m}`;
                } else {
                    groupKey = `${y}-${m}-${d}`;
                }
                
                if (!grouped[groupKey]) {
                    grouped[groupKey] = {
                        steps: 0,
                        calories: 0,
                        distance: 0,
                        floors: 0,
                        sleep_mins: 0,
                        sleep_days: 0,
                        weight: 0,
                        weight_days: 0,
                        body_fat: 0,
                        body_fat_days: 0,
                        muscle_mass: 0,
                        muscle_mass_days: 0,
                        bone_mass: 0,
                        bone_mass_days: 0,
                        hydration: 0,
                        hydration_days: 0,
                        spo2: 0,
                        spo2_days: 0,
                        body_temperature: 0,
                        body_temperature_days: 0,
                        count: 0
                    };
                }
                
                if (this.selectedGrouping === 'year') {
                    current.setFullYear(current.getFullYear() + 1);
                } else if (this.selectedGrouping === 'month') {
                    current.setMonth(current.getMonth() + 1);
                } else {
                    current.setDate(current.getDate() + 1);
                }
            }
        }

        filteredHistory.forEach(item => {
            let dayData = item.data;
            if (!dayData || !dayData.date) return;

            let dateParts = dayData.date.split('-');
            if (dateParts.length < 3) return;
            
            let year = dateParts[0];
            let month = dateParts[1];
            let day = dateParts[2];
            
            let groupKey;
            if (this.selectedGrouping === 'year') {
                groupKey = year;
            } else if (this.selectedGrouping === 'month') {
                groupKey = `${year}-${month}`;
            } else {
                groupKey = `${year}-${month}-${day}`;
            }

            if (!grouped[groupKey]) {
                grouped[groupKey] = {
                    steps: 0,
                    calories: 0,
                    distance: 0,
                    floors: 0,
                    sleep_mins: 0,
                    sleep_days: 0,
                    weight: 0,
                    weight_days: 0,
                    body_fat: 0,
                    body_fat_days: 0,
                    muscle_mass: 0,
                    muscle_mass_days: 0,
                    bone_mass: 0,
                    bone_mass_days: 0,
                    hydration: 0,
                    hydration_days: 0,
                    spo2: 0,
                    spo2_days: 0,
                    body_temperature: 0,
                    body_temperature_days: 0,
                    count: 0
                };
            }

            let s = parseInt(dayData.steps);
            let c = parseInt(dayData.calories);
            let d = parseFloat(dayData.distance);
            let f = parseInt(dayData.floors);
            let sleep = parseInt(dayData.sleep_duration);

            grouped[groupKey].steps += (isNaN(s) ? 0 : s);
            grouped[groupKey].calories += (isNaN(c) ? 0 : c);
            grouped[groupKey].distance += (isNaN(d) ? 0 : d);
            grouped[groupKey].floors += (isNaN(f) ? 0 : f);
            
            if (!isNaN(sleep) && sleep > 0) {
                grouped[groupKey].sleep_mins += sleep;
                grouped[groupKey].sleep_days += 1;
            }
            
            let w = parseFloat(dayData.weight);
            if (!isNaN(w) && w > 0) { grouped[groupKey].weight += w; grouped[groupKey].weight_days += 1; }
            let bf = parseFloat(dayData.body_fat);
            if (!isNaN(bf) && bf > 0) { grouped[groupKey].body_fat += bf; grouped[groupKey].body_fat_days += 1; }
            let mm = parseFloat(dayData.muscle_mass);
            if (!isNaN(mm) && mm > 0) { grouped[groupKey].muscle_mass += mm; grouped[groupKey].muscle_mass_days += 1; }
            let bm = parseFloat(dayData.bone_mass);
            if (!isNaN(bm) && bm > 0) { grouped[groupKey].bone_mass += bm; grouped[groupKey].bone_mass_days += 1; }
            let h = parseFloat(dayData.hydration);
            if (!isNaN(h) && h > 0) { grouped[groupKey].hydration += h; grouped[groupKey].hydration_days += 1; }
            let sp = parseFloat(dayData.spo2);
            if (!isNaN(sp) && sp > 0) { grouped[groupKey].spo2 += sp; grouped[groupKey].spo2_days += 1; }
            let bt = parseFloat(dayData.body_temperature);
            if (!isNaN(bt) && bt > 0) { grouped[groupKey].body_temperature += bt; grouped[groupKey].body_temperature_days += 1; }

            grouped[groupKey].count += 1;
        });

        let categories = Object.keys(grouped).sort();
        let formattedCategories = categories.map(k => this.formatDate(k));
        let seriesData = [];
        let seriesName = '';
        let yaxisTitle = '';
        let tooltipFormatter = null;

        if (this.selectedMetric === 'steps') {
            seriesName = this.$t('fitness.steps');
            yaxisTitle = this.$t('fitness.steps');
            categories.forEach(k => {
                seriesData.push(grouped[k].steps);
            });
            tooltipFormatter = function(val) {
                return numeral(val).format("0,000").replace(/,/g, "'") + " " + seriesName.toLowerCase();
            };
        } else if (this.selectedMetric === 'sleep') {
            seriesName = this.$t('fitness.average_sleep');
            yaxisTitle = 'Hours';
            categories.forEach(k => {
                let avg = grouped[k].sleep_days > 0 ? grouped[k].sleep_mins / grouped[k].sleep_days : 0;
                // Store as fractional hours for the chart line
                seriesData.push(parseFloat((avg / 60).toFixed(2)));
            });
            let _this = this;
            tooltipFormatter = function(val) {
                return _this.getHoursAndMinutesFromMinutes(val * 60);
            };
        } else if (this.selectedMetric === 'floors') {
            seriesName = this.$t('fitness.floors');
            yaxisTitle = this.$t('fitness.floors');
            categories.forEach(k => {
                seriesData.push(grouped[k].floors);
            });
            tooltipFormatter = function(val) {
                return numeral(val).format("0,000").replace(/,/g, "'") + " " + seriesName.toLowerCase();
            };
        } else if (this.selectedMetric === 'distance') {
            seriesName = this.$t('fitness.distance');
            yaxisTitle = this.$t('fitness.distance') + ' (km)';
            categories.forEach(k => {
                seriesData.push(parseFloat(grouped[k].distance.toFixed(2)));
            });
            tooltipFormatter = function(val) {
                return numeral(val).format("0,000.00").replace(/,/g, "'") + " km";
            };
        } else if (['weight', 'body_fat', 'muscle_mass', 'bone_mass', 'hydration', 'spo2', 'body_temperature'].includes(this.selectedMetric)) {
            let metricKey = this.selectedMetric;
            let metricDaysKey = metricKey + '_days';
            seriesName = this.$t('fitness.' + metricKey);
            let unit = metricKey === 'body_fat' || metricKey === 'spo2' ? ' %' : metricKey === 'body_temperature' ? ' °C' : ' kg';
            yaxisTitle = seriesName + ' (' + unit.trim() + ')';
            
            let lastKnownValue = null;
            
            categories.forEach(k => {
                let avg = grouped[k][metricDaysKey] > 0 ? grouped[k][metricKey] / grouped[k][metricDaysKey] : null;
                
                if (avg !== null) {
                    lastKnownValue = parseFloat(avg.toFixed(2));
                    seriesData.push(lastKnownValue);
                } else {
                    seriesData.push(lastKnownValue);
                }
            });
            tooltipFormatter = function(val) {
                return val !== null ? numeral(val).format("0.00").replace(/,/g, "'") + unit : "No data";
            };
        }

        if (seriesData.every(val => val === 0 || val === null)) {
            formattedCategories = [];
        }

        this.series = [{
            name: seriesName,
            data: seriesData
        }];

        this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
                categories: formattedCategories
            },
            yaxis: {
                title: {
                    text: yaxisTitle
                }
            },
            tooltip: {
                y: {
                    formatter: tooltipFormatter
                }
            },
            stroke: {
                show: true,
                width: ['sleep', 'weight', 'body_fat', 'muscle_mass', 'bone_mass', 'hydration', 'spo2'].includes(this.selectedMetric) ? 3 : 2,
                colors: ['sleep', 'weight', 'body_fat', 'muscle_mass', 'bone_mass', 'hydration', 'spo2'].includes(this.selectedMetric) ? ['#FFB024'] : ['transparent']
            },
            markers: {
                size: ['sleep', 'weight', 'body_fat', 'muscle_mass', 'bone_mass', 'hydration', 'spo2'].includes(this.selectedMetric) ? 4 : 0,
                colors: ['#FFB024'],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: {
                    size: 6
                }
            },
            fill: {
                opacity: 1,
                colors: ['#FFB024']
            }
        };
        // Reset type dynamically via chartType computed property
    }
  },
};
</script>

<style>
</style>